import React, { useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useStore } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { setCurrentEnvironment } from "@/actions/environments";
import { Button } from "@/design-system/components/Button/Button";
import { toast } from "@/design-system/components/Toast/Toast";
import { Typography } from "@/design-system/components/Typography/Typography";
import useCurrentEnvironment from "@/hooks/use-environment";
import { FreshpaintState } from "@/reducers";
import { useWizards } from "@/wizards/framework/WizardProvider";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { INSTALL_SDK_WIZARD_TYPE } from "@/wizards/installSdk/constants";
import { ADD_ENVIRONMENT_WIZARD_TYPE } from "../constants";
import { EnvironmentCard } from "../EnvironmentCard";
import { AddEnvironmentStepProps } from "../types";

const WHY_IT_MATTERS =
  "Each environment has its own snippet, events and destinations. The environment picker at the top of the sidebar shows the one you’re in, so check it before you copy a snippet or change settings.";

export function SwitchStep({ data, goToStep }: AddEnvironmentStepProps) {
  const currentEnvironment = useCurrentEnvironment();
  const { openWizard, discardWizard, findInProgressSession } = useWizards();
  const dispatch = useDispatch();
  const store = useStore<FreshpaintState>();
  const queryClient = useQueryClient();
  const history = useHistory();
  const location = useLocation();
  const installAfterSwitch = useRef<{ envID: string | null }>({ envID: null });

  // Wizard sessions are stored per environment, so this session can't follow
  // the switch. It's discarded, and Install Freshpaint opens in the new
  // environment instead. This step unmounts in the same commit that changes
  // the environment, and the wizard provider queues its reload of the new
  // environment's sessions during that commit's effects. Opening on the next
  // tick therefore lands the install wizard after that reload, not before it.
  useEffect(() => {
    const pending = installAfterSwitch.current;
    return () => {
      const { envID } = pending;
      if (!envID) {
        return;
      }
      window.setTimeout(() => {
        if (store.getState().environments.currentEnvironment?.ID === envID) {
          openWizard(INSTALL_SDK_WIZARD_TYPE);
        }
      }, 0);
    };
  }, [openWizard, store]);

  const created = data.created;
  if (!created) {
    return (
      <WizardStepLayout
        title="Create it first"
        description="Choose a name for your project or environment, then switch to it here."
        primaryAction={{
          label: "Choose a name",
          onClick: () => goToStep("choose"),
        }}
      />
    );
  }

  const discardThisSession = () => {
    const session = findInProgressSession(ADD_ENVIRONMENT_WIZARD_TYPE);
    if (session) {
      discardWizard(session.id);
    }
  };

  const target = `${created.projectName} › ${created.envName}`;

  const switchToCreated = (thenInstall: boolean) => {
    installAfterSwitch.current.envID = thenInstall ? created.envID : null;
    discardThisSession();
    dispatch(setCurrentEnvironment(created.envID, queryClient));
    // Settings pages scope the sidebar picker to their own environment, so
    // the switch would not show there.
    if (location.pathname.startsWith("/settings")) {
      history.push("/");
    }
    toast.success(`Switched to ${target}`);
  };

  return (
    <WizardStepLayout
      title={`Switch to ${created.envName}`}
      description={WHY_IT_MATTERS}
      primaryAction={{
        label: "Switch and install Freshpaint",
        onClick: () => switchToCreated(true),
      }}
      secondaryActions={
        <Button variant="outline" onClick={() => switchToCreated(false)}>
          Switch only
        </Button>
      }
    >
      <div className="flex items-center gap-4">
        {currentEnvironment && (
          <>
            <EnvironmentCard
              label="You’re working in"
              projectName={currentEnvironment.project.name}
              envName={currentEnvironment.name}
            />
            <ArrowRight
              className="text-iconMinimal size-icon-md shrink-0"
              aria-hidden
            />
          </>
        )}
        <EnvironmentCard
          label="Switch to"
          projectName={created.projectName}
          envName={created.envName}
          highlighted
          isNew
        />
      </div>
      <Typography variant="body-sm" color="subtle">
        After switching, Install Freshpaint opens in {created.envName} to walk
        you through copying its snippet, adding it to your site or Google Tag
        Manager, and checking events arrive in Live View.
      </Typography>
    </WizardStepLayout>
  );
}
